<script>
  import { onMount, createEventDispatcher } from 'svelte';

  import { deepEqual, getRef, initSchemaValues, _ } from './utils.js';

  import Image from './image.svelte';
  import Images from './images.svelte';
  import Choices from './choices.svelte';
  import ArrayComponent from './array.svelte';
  import Treepath from './treepath.svelte';
  import Fields from './fields.svelte';

  import { isObject } from './utils.js';

  export let openapi;
  export let id;
  export let schema;
  export let required = false;
  export let disabled = undefined;
  export let value = undefined;
  export let error = false;
  export let dirty;
  export let canBeSubmitted;
  export let loading;
  export let currentPath;
  export let texts = undefined;
  export let extras = undefined;

  let originalValue;

  let type = 'text';

  let anyOfSchemas = {};
  let anyOf;

  let showPassword = false;
  let passwordStrength;
  const strengthTexts = {
    lowercase: "A lowercase character",
    uppercase: "An uppercase character",
    symbol: "A Symbol",
    number: "A Number"
  }

  $: value, checks();

  const checks = () => {
    if (schema.format === 'password' && !schema.dontCheckStrength && value ) {
      passwordStrength = checkPasswordStrength.passwordStrength(value);
    }

    if (originalValue) {
      if (!dirty) dirty = {}
      if (originalValue instanceof File) {
        if (originalValue.name !== value.name || originalValue.lastModifiedDate.toString() !== value.lastModifiedDate.toString() || originalValue.size !== value.size || originalValue.type !== value.type) dirty[id] = true;
        else delete dirty[id];
      } else if (!deepEqual(originalValue, value)) dirty[id] = true;
    } else if (dirty && Object.keys(dirty).includes(id)) delete dirty[id];
  }

  const init = async () => {
    if (value === undefined) {
      switch (schema.type) {
        case 'number':
          if (schema.minimum && schema.maximum && (schema.maximum - schema.minimum) < 1000) {
            value = 0;
          }
          break;
        case 'integer':
          if (schema.minimum && schema.maximum && (schema.maximum - schema.minimum) < 1000) {
            value = 0;
          }
          break;
        case 'boolean':
          value = false;
          break;
        case 'array':
          value = [];
          break;
        case 'object':
          value = {};
          break;
      }

      if (schema.default) {
        if (schema.format === 'date-time' && schema.default === 'now') {
          const now = new Date();
          now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
          now.setSeconds(0);
          now.setMilliseconds(0);
          value = now.toISOString().slice(0, -1);
        } else {
          value = schema.default;
        }
      }
    } else {
      if (schema.format === 'image' && typeof value === 'string') {
        const resp = await fetch(value);
        if (resp.ok) {
          const blob = await resp.blob();
          value = new File([blob], value.split("/").pop(), { type: resp.headers.get("Content-type") });
        } else {
          value = undefined;
          return;
        }
      }

      if (value instanceof File) originalValue = new File([value], value.name, { type: value.type });
      else if (isObject(value)) originalValue = { ...value };
      else if (Array.isArray(value)) originalValue = value.length ? [ ...value ] : undefined;
      else originalValue = value;
    }

    if (schema.anyOf) {
      for (const ref of schema.anyOf) {
        anyOfSchemas[ref.$ref] = getRef(openapi, ref.$ref);
      }
    }
  }

  const addArrayWidget = ({ detail }) => {
    value.push(detail);
    value = value;
  }

  const removeArrayWidget = ({ detail }) => {
    value.splice(value.indexOf(detail), 1);
    value = value;
  }

  const setValue = val => { value = val };

  onMount(init);

  const dispatch = createEventDispatcher();
</script>

{#if schema.type === 'boolean'}
  <input { id } type="checkbox" class:checkbox={ !schema.format } class:swap-checkbox={ schema.format === 'swap' } { required } class:has-error={ error } { disabled } bind:checked={ value } />
{:else if schema.format === 'image'}
  <Image { schema } { id } { error } bind:value={ value } />
{:else if schema.format === 'images'}
  <Images { schema } { id } accept={ schema.accept } bind:value={ value } />
{:else if schema.format === 'choices'}
  <Choices { schema } { id } bind:value={ value } />
{:else if schema.type === 'array'}
  {#if schema.widget}
    <slot name="array-widgets" { addArrayWidget } { removeArrayWidget } />
  {:else}
    <ArrayComponent { openapi } { id } { schema } { required } { disabled } { error } { texts } { extras } bind:value={ value }>

      <svelte:fragment slot="array-widgets" let:addArrayWidget={ addArrayWidget } let:removeArrayWidget={ removeArrayWidget }>
        <slot name="array-widgets" { addArrayWidget } { removeArrayWidget }></slot>
      </svelte:fragment>
      <svelte:fragment slot="wallet-yours" let:value={ value } let:setValue={ setValue }>
        <slot name="wallet-yours" { value } { setValue } />
      </svelte:fragment>

    </ArrayComponent>
  {/if}
{:else if schema.format === 'date-time'}
  <input { id } type="datetime-local" placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } { required } { disabled } class:has-error={ error } bind:value={ value } />
{:else if schema.format === 'date'}
  <input { id } type="date" placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } { required } { disabled } class:has-error={ error } bind:value={ value } />
{:else if schema.format === 'password'}
  <label class="lt-password">
    {#if showPassword}
      <input { id } type="text" placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } { required } { disabled } class:has-error={ error } class:password-text={ true } bind:value={ value } />
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <i class="bi bi-eye-slash" on:click={ () => { showPassword = false } }></i>
    {:else}
      <input { id } type="password" placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } { required } { disabled } class:has-error={ error } bind:value={ value } />
      <!-- svelte-ignore a11y-click-events-have-key-events -->
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <i class="bi bi-eye" on:click={ () => { showPassword = true } }></i>
    {/if}
  </label>
  {#if !schema.dontCheckStrength && value}
    <div class="password-strength-widget { passwordStrength.value.toLowerCase().replace(' ', '_') }">
      <div class="password-strength">
        <div>&nbsp;</div><div>&nbsp;</div><div>&nbsp;</div>
      </div>
      <div class="contains">
        {#each ["lowercase", "uppercase", "symbol", "number"] as content}
          <span class:done={ passwordStrength.contains.includes(content) }>
            { strengthTexts[content] }
          </span>
        {/each}
      </div>
    </div>
  {/if}
{:else if schema.format === 'uri'}
  <input { id } type="url" placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } { required } { disabled } class:has-error={ error } bind:value={ value } />
{:else if schema.format === 'treepath'}
  <Treepath { id } { schema } { required } { disabled } { error } { texts } { extras } bind:value={ value } />
{:else if schema.format === 'phone'}
  <input { id } type="phone" placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } { required } { disabled } class:has-error={ error } bind:value={ value } />
{:else if schema.format === 'wallet'}
  <div class="wallet-field">
    <input { id } type="text" placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } { required } { disabled } class:has-error={ error } bind:value={ value } />
    <slot name="wallet-yours" { value } { setValue } />
  </div>
{:else if schema.format === 'HTML'}
  <textarea { id } { required } { disabled } placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } is="highlighted-code" language={ schema.language } auto-height={ schema.autoHeight } class:has-error={ error } rows={ schema.rows || 5 } bind:value={ value }></textarea>
{:else if type === 'text' && schema.maxLength > 75}
  <textarea { id } { required } { disabled } placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } class:has-error={ error } rows={ schema.rows || 5 } bind:value={ value }></textarea>
{:else if schema.allOf}
  {#each schema.allOf as model}
    {@const realSchema = getRef(openapi, model.$ref)}
    {#if realSchema.properties}
      <Fields
        { openapi } schema={ realSchema } { currentPath } errors={ error } { texts } { extras }
        bind:value={ value } bind:canBeSubmitted={ canBeSubmitted } bind:loading={ loading }
        bind:dirty={ dirty }
        on:message={ ({ detail }) => { dispatch('message', detail) } }
      >

        <svelte:fragment slot="array-widgets" let:addArrayWidget={ addArrayWidget } let:removeArrayWidget={ removeArrayWidget }>
          <slot name="array-widgets" { addArrayWidget } { removeArrayWidget }></slot>
        </svelte:fragment>
        <svelte:fragment slot="wallet-yours" let:value={ value } let:setValue={ setValue }>
          <slot name="wallet-yours" { value } { setValue } />
        </svelte:fragment>

      </Fields>
    {:else if realSchema.enum}
      <svelte:self { openapi } { id } schema={ realSchema } { required } { disabled } bind:value={ value } bind:canBeSubmitted={ canBeSubmitted } bind:loading={ loading } bind:dirty={ dirty } { error } { texts } { extras }>

        <svelte:fragment slot="array-widgets" let:addArrayWidget={ addArrayWidget } let:removeArrayWidget={ removeArrayWidget }>
          <slot name="array-widgets" { addArrayWidget } { removeArrayWidget }></slot>
        </svelte:fragment>
        <svelte:fragment slot="wallet-yours" let:value={ value } let:setValue={ setValue }>
          <slot name="wallet-yours" { value } { setValue } />
        </svelte:fragment>

      </svelte:self>
    {/if}
  {/each}
{:else if schema.anyOf}
  <div class="anyOfEnum">
    {#each Object.entries(anyOfSchemas) as [ref, sch]}
      <label>
        <span>{ sch.title }</span>
        <input type="radio" bind:group={ anyOf } value={ ref } on:click={ () => { value = initSchemaValues(anyOfSchemas[ref]) } } />
      </label>
    {/each}
    {#if anyOf}
      <Fields
        { openapi } schema={ anyOfSchemas[anyOf] } { currentPath } errors={ error } { extras }
        bind:value={ value } bind:canBeSubmitted={ canBeSubmitted } bind:loading={ loading }
        bind:dirty={ dirty }
        on:message={ ({ detail }) => { dispatch('message', detail) } }
      >

        <svelte:fragment slot="array-widgets" let:addArrayWidget={ addArrayWidget } let:removeArrayWidget={ removeArrayWidget }>
          <slot name="array-widgets" { addArrayWidget } { removeArrayWidget }></slot>
        </svelte:fragment>
        <svelte:fragment slot="wallet-yours" let:value={ value } let:setValue={ setValue }>
          <slot name="wallet-yours" { value } { setValue } />
        </svelte:fragment>

      </Fields>
    {/if}
  </div>
{:else if schema.$ref}
  {@const realSchema = getRef(openapi, schema.$ref)}
  {#if realSchema.enum}
    <svelte:self { openapi } { id } schema={ realSchema } { required } { disabled } bind:value={ value } bind:canBeSubmitted={ canBeSubmitted } bind:loading={ loading } bind:dirty={ dirty } { error } { texts } { extras }>

      <svelte:fragment slot="array-widgets" let:addArrayWidget={ addArrayWidget } let:removeArrayWidget={ removeArrayWidget }>
        <slot name="array-widgets" { addArrayWidget } { removeArrayWidget }></slot>
      </svelte:fragment>
      <svelte:fragment slot="wallet-yours" let:value={ value } let:setValue={ setValue }>
        <slot name="wallet-yours" { value } { setValue } />
      </svelte:fragment>

    </svelte:self>
  {:else}
    <Fields
      { openapi } schema={ realSchema } { currentPath } errors={ error } { texts } { extras }
      bind:value={ value } bind:canBeSubmitted={ canBeSubmitted } bind:loading={ loading }
      bind:dirty={ dirty }
      on:message={ ({ detail }) => { dispatch('message', detail) } }
    >

      <svelte:fragment slot="array-widgets" let:addArrayWidget={ addArrayWidget } let:removeArrayWidget={ removeArrayWidget }>
        <slot name="array-widgets" { addArrayWidget } { removeArrayWidget }></slot>
      </svelte:fragment>
      <svelte:fragment slot="wallet-yours" let:value={ value } let:setValue={ setValue }>
        <slot name="wallet-yours" { value } { setValue } />
      </svelte:fragment>

    </Fields>
  {/if}
{:else if schema.enum}
  {#if schema.compact || schema.enum.length > 5}
    <select { id } { required } { disabled } class:has-error={ error } bind:value={ value }>
      <option value={ undefined } disabled>{ schema.banner || 'Select one' }</option>
      {#each schema.enum as item}
        {#if isObject(item)}
          <option value={ item[schema.valueField || 'value'] }>
            { item[schema.labelField || 'label'] }
          </option>
        {:else}
          <option>{ item }</option>
        {/if}
      {/each}
    </select>
  {:else}
    <div class="enumField">
      {#each schema.enum as item}
        <label>
          {#if isObject(item)}
            <span>{ item[schema.labelField || 'label'] }</span>
            <input type="radio" class:has-error={ error } bind:group={ value } value={ item[schema.valueField || 'value'] } />
          {:else}
            <span>{ item }</span>
            <input type="radio" class:has-error={ error } bind:group={ value } value={ item } />
          {/if}
        </label>
      {/each}
    </div>
  {/if}
{:else if schema.hidden}
  <input { id } type="hidden" { required } bind:value={ value } />
{:else if [ 'integer', 'number' ].includes(schema.type) && schema.minimum && schema.maximum && (schema.maximum - schema.minimum) < 1000}
  <input { id } type="range" placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } max={ schema.maximum} min={ schema.minimum } { required } { disabled } class:has-error={ error } bind:value={ value } />
  <output for={ id } class="tooltip">{ value }</output>
{:else if schema.type === 'integer'}
  <input { id } type="number" placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } min={ schema.min } max={ schema.max } step="1" { required } { disabled } class:has-error={ error } bind:value={ value } />
{:else if schema.type === 'number'}
  <input { id } type="number" placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } min={ schema.min } max={ schema.max } step="ANY" { required } { disabled } class:has-error={ error } bind:value={ value } />
{:else}
  <input { id } type="text" placeholder={ _(`fields.${ id }.placeholder`, texts, schema.placeholder) } { required } { disabled } class:has-error={ error } bind:value={ value } />
{/if}
